import {
  CircularProgress,
  Chip,
  Tooltip,
  makeStyles,
  Theme,
} from '@material-ui/core';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { useTranslation } from 'react-i18next';
import { type ContextFileStatus } from './types';

const useStyles = makeStyles((theme: Theme) => ({
  ready: {
    backgroundColor: theme.palette.success?.main ?? '#4caf50',
    color: theme.palette.common.white,
  },
  inProgress: {
    backgroundColor: theme.palette.warning?.main ?? '#ff9800',
    color: theme.palette.common.white,
  },
  failed: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.common.white,
  },
  superseded: {
    backgroundColor: theme.palette.grey[400],
    color: theme.palette.common.white,
  },
  spinner: {
    marginLeft: theme.spacing(0.5),
    color: theme.palette.common.white,
    verticalAlign: 'middle',
  },
  errorIcon: {
    fontSize: 14,
    marginLeft: theme.spacing(0.5),
    verticalAlign: 'middle',
  },
}));

const IN_PROGRESS_STATUSES: ContextFileStatus[] = [
  'PENDING',
  'QUEUED',
  'VALIDATING',
  'EXTRACTING',
  'INDEXING',
];

type Props = {
  status: ContextFileStatus;
  errorReport?: string | null;
};

export function ContextFileStatusChip({ status, errorReport }: Props) {
  const classes = useStyles();
  const { t } = useTranslation('projects');

  const label = t(`fileStatus.${status}`, { defaultValue: status });

  if (status === 'READY') {
    return <Chip size="small" label={label} className={classes.ready} />;
  }

  if (status === 'SUPERSEDED') {
    return <Chip size="small" label={label} className={classes.superseded} />;
  }

  if (IN_PROGRESS_STATUSES.includes(status)) {
    return (
      <Chip
        size="small"
        label={
          <>
            {label}
            <CircularProgress size={10} className={classes.spinner} />
          </>
        }
        className={classes.inProgress}
      />
    );
  }

  // FAILED
  const chip = (
    <Chip
      size="small"
      label={
        <>
          {label}
          <ErrorOutlineIcon className={classes.errorIcon} />
        </>
      }
      className={classes.failed}
    />
  );

  if (errorReport) {
    return <Tooltip title={errorReport}>{chip}</Tooltip>;
  }

  return chip;
}
